import { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft } from '@fortawesome/free-solid-svg-icons';
import { ChartData, ChartOptions } from 'chart.js';
import i18n from 'i18n';
import { GenericChart } from 'components/common/Chart/GenericChart';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { Modal } from 'components/common/Modal/Modal';
import { HEALTH_STATE_CLASS } from 'components/common/Tags/Tags';
import { useCallAPI } from 'hooks/useCallAPI';
import {
  DeviceUnitDetail,
  DeviceUnitLevel,
  DeviceUnitRecord
} from 'core/deviceManagement/DeviceManagement';
import {
  DefaultDeviceManagementManager,
  DeviceManagementManager
} from 'core/deviceManagement/DeviceManagementManager';
import { DeviceStatusChip, resolveDeviceHealthState } from './DeviceStatusChip';
import {
  formatCoordinates,
  buildFootTrafficPeakLabel,
  FOOT_TRAFFIC_BAR_COLOR,
  formatFootTraffic,
  formatResolution,
  hasFootTrafficReadings,
  NO_VALUE,
  resolveStatusNote,
  toFootTrafficChartData
} from './deviceDetail';
import { DEVICE_LIST_DATE_FORMAT } from './deviceListColumns';
import { buildWeeklyBusinessHours } from './businessHoursFormat';
import styles from './deviceManagement.module.scss';

type DeviceDetailPanelProps = {
  /**
   * unitKey 在不同渠道底下可能重複，而且要聚合哪些版位取決於渠道的投遞單位，
   * 所以查詳情一定要連渠道一起帶。
   */
  channelId: number;
  /**
   * 清單上被點開的那一列。店號、地址、營業時間與機台特徵顯示的是這一列的值，
   * 不讀詳情端點回來的那一份。
   */
  record: DeviceUnitRecord;
  onClose: () => void;
  manager?: DeviceManagementManager;
  /**
   * 接在基本資訊後面的區塊。詳情本身兩個分頁共用，只有機台可售狀況那一頁會再掛
   * 一份月曆上去，所以由呼叫端傳進來，而不是在這裡判斷自己開在哪一個分頁。
   */
  extraContent?: React.ReactNode;
};

const defaultManager = new DefaultDeviceManagementManager();

const DetailRow: React.FC<
  React.PropsWithChildren<{ label: string; testId: string }>
> = ({ label, testId, children }) => (
  <>
    <div className={styles.detailLabel}>{label}</div>
    <div className={styles.detailValue} data-testid={testId}>
      {children}
    </div>
  </>
);

/**
 * 詳情面板。走共用的 `Modal` 再用一個 class 把對話框釘到右側整條 —— 這一版的
 * Bootstrap 沒有 offcanvas，右側面板在這個 repo 就是這樣做的。
 *
 * Modal 是畫在 portal 裡的，底下的清單留在原地不卸載，捲動位置與頁碼都還在。
 */
export const DeviceDetailPanel: React.FC<DeviceDetailPanelProps> = ({
  channelId,
  record,
  onClose,
  manager = defaultManager,
  extraContent
}) => {
  const { loading, callAPIs } = useCallAPI();
  const [detail, setDetail] = useState<DeviceUnitDetail>();
  const unitKey = record.unitKey;

  useEffect(() => {
    callAPIs([() => manager.getUnitDetail(channelId, unitKey)], (result) => {
      setDetail(result);
    });
  }, [channelId, unitKey, callAPIs, manager]);

  const chart = useMemo(() => {
    if (!detail) {
      return undefined;
    }
    const { labels, values } = toFootTrafficChartData(detail.last7DaysFootfall);
    const data: ChartData<'bar', (number | null)[], string> = {
      labels,
      datasets: [
        {
          label: i18n.t('deviceManagement.labels.weeklyFootTraffic'),
          data: values,
          backgroundColor: FOOT_TRAFFIC_BAR_COLOR
        }
      ]
    };
    const options: ChartOptions<'bar'> = {
      plugins: { legend: { display: false }, datalabels: { display: false } },
      scales: { y: { beginAtZero: true } }
    };
    return { data, options };
  }, [detail]);

  const weeklyBusinessHours = buildWeeklyBusinessHours(record.businessHours);

  const footTrafficPeak = useMemo(
    () =>
      detail ? buildFootTrafficPeakLabel(detail.last7DaysFootfall) : undefined,
    [detail]
  );

  // 說明的顏色跟著狀態走（與狀態 chip 同一組），內容的來源也是狀態決定的。
  const statusNote = useMemo(() => {
    if (!detail) {
      return undefined;
    }
    const state = resolveDeviceHealthState(detail.healthState);
    const content = resolveStatusNote(state, detail.healthStateReason);
    if (!content) {
      return undefined;
    }
    return { className: HEALTH_STATE_CLASS[state], content };
  }, [detail]);

  const renderTitle = useCallback(
    () => (
      <div className={styles.detailHeader}>
        <span
          className={styles.detailBack}
          onClick={onClose}
          data-testid='device-detail-back'
        >
          <FontAwesomeIcon icon={faAngleLeft} />
          {i18n.t('common.buttons.goBack')}
        </span>
        <span className={styles.detailTitle}>
          {i18n.t('deviceManagement.labels.detailTitle')}
        </span>
      </div>
    ),
    [onClose]
  );

  return (
    <Modal
      className={styles.detailModal}
      renderTitle={renderTitle}
      dismiss={onClose}
      animation={false}
    >
      <div data-testid='device-detail-panel'>
        {loading && <LoadingIndicator />}
        <div className={styles.detailUnitName} data-testid='detail-unit-name'>
          {record.unitName}
        </div>
        {detail && (
          <>
            <div className={styles.detailStatusRow} data-testid='detail-status'>
              <DeviceStatusChip
                healthState={detail.healthState}
                unitKey={detail.unitKey}
              />
              <span
                className={styles.detailLastReported}
                data-testid='detail-last-reported'
              >
                {i18n.t('deviceManagement.labels.lastReportedAtValue', {
                  value: detail.lastReportedAt
                    ? moment(detail.lastReportedAt).format(
                        DEVICE_LIST_DATE_FORMAT
                      )
                    : NO_VALUE
                })}
              </span>
            </div>
            {statusNote && (
              <div
                className={`${styles.statusNote} ${styles[statusNote.className]}`}
                data-testid='detail-status-note'
              >
                <div className={styles.statusNoteTitle}>
                  {i18n.t('deviceManagement.labels.statusReason')}
                </div>
                <div data-testid='detail-status-reason'>
                  {statusNote.content}
                </div>
              </div>
            )}
            <section className={styles.detailSection}>
              <h2>{i18n.t('deviceManagement.labels.basicInformation')}</h2>
              <div className={styles.detailGrid}>
                <DetailRow
                  label={i18n.t('deviceManagement.headers.storeNum')}
                  testId='detail-store-num'
                >
                  {record.storeNum || NO_VALUE}
                </DetailRow>
                <DetailRow
                  label={i18n.t('deviceManagement.headers.address')}
                  testId='detail-address'
                >
                  {record.address || NO_VALUE}
                </DetailRow>
                <DetailRow
                  label={i18n.t('deviceManagement.labels.businessHours')}
                  testId='detail-business-hours'
                >
                  {weeklyBusinessHours
                    ? weeklyBusinessHours.map((row) => (
                        <div key={row.dayOfWeek}>{row.text}</div>
                      ))
                    : NO_VALUE}
                </DetailRow>
                <DetailRow
                  label={i18n.t('deviceManagement.labels.region')}
                  testId='detail-region'
                >
                  {detail.region || NO_VALUE}
                </DetailRow>
                <DetailRow
                  label={i18n.t('limitation.labels.administrativeArea')}
                  testId='detail-district'
                >
                  {detail.district || NO_VALUE}
                </DetailRow>
                {/* 階層標籤由後端組好（`parent > child`），前端不要再組一份。 */}
                <DetailRow
                  label={i18n.t('limitation.labels.outdoorPlaceCategories')}
                  testId='detail-place-categories'
                >
                  {detail.placeCategoryLabels.length > 0 ? (
                    <div className={styles.detailTags}>
                      {detail.placeCategoryLabels.map((category) => (
                        <span className={styles.detailTag} key={category}>
                          {category}
                        </span>
                      ))}
                    </div>
                  ) : (
                    NO_VALUE
                  )}
                </DetailRow>
                {/* 機台特徵只有機台層的渠道才有；門市層沒有這個維度。 */}
                {record.unitLevel === DeviceUnitLevel.DEVICE && (
                  <DetailRow
                    label={i18n.t(
                      'deviceManagement.headers.deviceCharacteristic'
                    )}
                    testId='detail-device-characteristic'
                  >
                    {record.deviceCharacteristic || NO_VALUE}
                  </DetailRow>
                )}
                <DetailRow
                  label={i18n.t('deviceManagement.labels.coordinates')}
                  testId='detail-coordinates'
                >
                  {formatCoordinates(detail.latitude, detail.longitude)}
                </DetailRow>
                {/* 解析度是渠道層的設定，同一個渠道底下每台機器都顯示同一組值。 */}
                <DetailRow
                  label={i18n.t('deviceManagement.labels.resolution')}
                  testId='detail-resolution'
                >
                  {formatResolution(
                    detail.screenPixelWidth,
                    detail.screenPixelHeight
                  )}
                </DetailRow>
                <DetailRow
                  label={i18n.t(
                    'deviceManagement.headers.dailyAverageFootfall'
                  )}
                  testId='detail-daily-foot-traffic'
                >
                  {formatFootTraffic(detail.dailyAverageFootfall)}
                </DetailRow>
              </div>
              <div className={styles.detailChartBlock}>
                <div className={styles.detailChartHeading}>
                  <span className={styles.detailLabel}>
                    {i18n.t('deviceManagement.labels.weeklyFootTraffic')}
                  </span>
                  {/* 長條圖只給得出相對高低，這一句補絕對量級與「哪一天最多」。 */}
                  {footTrafficPeak && (
                    <span
                      className={styles.detailChartPeak}
                      data-testid='foot-traffic-peak'
                    >
                      {footTrafficPeak}
                    </span>
                  )}
                </div>
                {hasFootTrafficReadings(detail.last7DaysFootfall) && chart ? (
                  <GenericChart<'bar'>
                    type='bar'
                    data={chart.data as ChartData<'bar', number[], string>}
                    options={chart.options}
                    height={160}
                  />
                ) : (
                  <div
                    className={styles.detailEmptyChart}
                    data-testid='detail-foot-traffic-empty'
                  >
                    {i18n.t('deviceManagement.messages.noFootTrafficData')}
                  </div>
                )}
              </div>
            </section>
            {extraContent}
          </>
        )}
      </div>
    </Modal>
  );
};
